import styles from './recipient-input.module.css'

import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { UserSearchResult } from '@disco/api/server'
import { getEmailHandleFromAngularBrackets } from '@disco/utils/email'
import { validateEmail } from '@disco/utils/validation'

import { Badge, Box, Flex as DsFlex, Text } from 'components/ds'

import useEventListener from 'hooks/use-event-listener'

import { RecipientDropdown, SearchOption } from './recipient-dropdown'
import { useRecipientSearch } from './use-recipient-search'

/**
 * Recipient tag interface for UI representation
 */
interface RecipientTag {
  /** Unique identifier for the recipient tag */
  id: string
  /** Email address of the recipient */
  email?: string
  /** Display name of the recipient */
  name?: string
  /** DISCO contact id */
  contactId?: number
  /** Whether the email address is valid */
  isValid: boolean
}

/**
 * Props for the RecipientInput component
 */
interface RecipientInputProps {
  /** Array of current recipient tags */
  recipients: RecipientTag[]
  /** Called when a recipient is added. `contactId` is set when picked from
   * the dropdown; undefined for new entries. */
  onAdd: (email?: string, name?: string, contactId?: number) => void
  /** Callback function when a recipient is removed */
  onRemove: (id: string) => void
  /** Optional placeholder text for the input field */
  placeholder?: string
  /** Optional ARIA label for accessibility */
  'aria-label'?: string
  /** Allows creation of new recipients */
  allowCreateRecipient?: boolean

  /** Whether search returns contacts without emails */
  includeBlankEmails?: boolean
}

const resultFilter = (result: UserSearchResult) =>
  ['email', 'user', 'saved', 'brief'].includes(result.type)

/**
 * RecipientInput - A multi-select email input component with autocomplete
 *
 * This component provides a tag-based interface for adding and managing email recipients.
 * It features autocomplete suggestions from the DISCO user/business database, email validation,
 * keyboard navigation, bulk paste support, and accessibility support.
 *
 * Features:
 * - Multi-select email input with visual tags
 * - Real-time autocomplete suggestions
 * - Email validation with visual feedback
 * - Bulk paste support for delimiter-separated email lists (space, comma, semicolon, tab, newline)
 * - Keyboard navigation (arrows, enter, comma, backspace, tab)
 * - Click-to-remove recipient tags with hover states
 * - Container scrolling with arrow keys when dropdown is closed
 * - Accessibility compliance with ARIA attributes
 * - Debounced search (300ms) to optimize API calls
 * - Prevention of duplicate email addresses
 * - Built with new design system components
 *
 * Keyboard shortcuts:
 * - Enter/Comma: Add current input as recipient
 * - Backspace: Remove last recipient when input is empty
 * - Tab: Add current input and move focus
 * - Escape: Close dropdown and reset selection
 * - Up/Down arrows: Navigate dropdown or scroll recipient container
 *
 * Paste behavior:
 * - Paste single email: Normal paste behavior
 * - Paste multiple emails: Automatically parses and validates emails separated by:
 *   space, comma, semicolon, tab, or newline. Invalid emails are excluded.
 *
 * @param props - Component props
 * @returns RecipientInput component
 *
 * @example
 * ```tsx
 * <RecipientInput
 *   recipients={recipientTags}
 *   onAdd={(email) => addRecipient(email)}
 *   onRemove={(id) => removeRecipient(id)}
 *   placeholder="Enter email addresses..."
 *   aria-label="Brief recipients"
 * />
 * ```
 */
export const RecipientInput: React.FC<RecipientInputProps> = ({
  recipients,
  onAdd,
  onRemove,
  placeholder = 'Enter email addresses...',
  'aria-label': ariaLabel = 'Email recipients',
  allowCreateRecipient = false,
  includeBlankEmails = false,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [clickedOutside, setClickedOutside] = useState<boolean>(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isSelectingFromDropdown = useRef(false)
  const inputId = useId()

  const { searchRecipients, suggestions, loading, apiError, cleanup } =
    useRecipientSearch({
      includeBlankEmails,
      hideBusinesses: false,
      includeBriefs: true,
      autoSuggest: true,
      resultFilter,
    })

  // Transform UserSearchResult[] to SearchOption[] format
  const dropdownOptions = useMemo<SearchOption[]>(() => {
    return suggestions
      .map(suggestion => {
        // Create base SearchOption structure
        const baseOption = {
          id: suggestion.id.toString(),
          type: suggestion.type,
        }

        // Handle different types of UserSearchResult
        switch (suggestion.type) {
          case 'user':
            return {
              ...baseOption,
              value: suggestion.email,
              label: suggestion.name || suggestion.email,
              name: suggestion.name,
              email: suggestion.email,
            }
          case 'saved':
            return {
              ...baseOption,
              value: suggestion.name,
              label: suggestion.name,
              name: suggestion.name,
              email: '', // Saved groups don't have a single email, use empty string as placeholder
              recipients: suggestion.recipients,
            }
          case 'brief':
            return {
              ...baseOption,
              value: suggestion.name,
              label: suggestion.name,
              name: suggestion.name,
              email: '', // Briefs don't have a single email, use empty string as placeholder
              recipients: suggestion.recipients,
            }
          default:
            // Fallback for any other types (including 'email' and 'error')
            return {
              ...baseOption,
              value:
                (suggestion as any).email || (suggestion as any).name || '',
              label:
                (suggestion as any).name || (suggestion as any).email || '',
              name: (suggestion as any).name || '',
              email: (suggestion as any).email || '',
              ...((suggestion as any).type === 'error' && { error: true }),
            }
        }
      })
      .filter((option: SearchOption) => {
        if (allowCreateRecipient && option.type === 'email') {
          return false
        }

        // OR-match — different lists surface different id fields for the same
        // person, so email/name are the bridge when contactIds diverge.
        const matchesPill = (
          pill: RecipientTag,
          candidate: {
            id?: string | number
            contact_id?: number | null
            email?: string
            name?: string
            type?: string
          },
        ) => {
          const rawCandidateId =
            candidate.contact_id != null
              ? candidate.contact_id
              : candidate.type === 'user' && candidate.id != null
              ? Number.parseInt(String(candidate.id), 10)
              : null
          const candidateContactId =
            typeof rawCandidateId === 'number' &&
            Number.isFinite(rawCandidateId)
              ? rawCandidateId
              : null

          if (
            pill.contactId != null &&
            candidateContactId != null &&
            pill.contactId === candidateContactId
          ) {
            return true
          }

          const pillEmail = pill.email?.toLowerCase()
          const candidateEmail = candidate.email?.toLowerCase()
          if (pillEmail && candidateEmail && pillEmail === candidateEmail) {
            return true
          }

          // Name match only when neither side has an id — otherwise two
          // different Alices would collapse.
          if (pill.contactId == null && candidateContactId == null) {
            const pillName = pill.name?.toLowerCase()
            const candidateName = candidate.name?.toLowerCase()
            if (pillName && candidateName && pillName === candidateName) {
              return true
            }
          }

          return false
        }

        if (
          (option.type === 'saved' || option.type === 'brief') &&
          'recipients' in option
        ) {
          const allRecipientsSelected = option.recipients.every(recipient =>
            recipients.find(pill => matchesPill(pill, recipient)),
          )
          return !allRecipientsSelected
        }

        return !recipients.find(pill => matchesPill(pill, option))
      })
  }, [suggestions, recipients, allowCreateRecipient])

  const trimmedInput = inputValue.trim()

  const canCreate = allowCreateRecipient
    ? !!trimmedInput
    : validateEmail(trimmedInput)

  const shouldShowCreate =
    canCreate &&
    !dropdownOptions.some(
      s => s.email?.toLowerCase() === trimmedInput.toLowerCase(),
    )

  const showDropdown =
    (loading || dropdownOptions.length > 0 || !!apiError || shouldShowCreate) &&
    !clickedOutside

  const suggestionAt = useCallback(
    (index: number) => {
      if (allowCreateRecipient && shouldShowCreate) {
        if (index === 0) return null
        return dropdownOptions[index - 1] ?? null
      }
      if (index < dropdownOptions.length) return dropdownOptions[index]
      return null
    },
    [dropdownOptions, allowCreateRecipient, shouldShowCreate],
  )

  // Search suggestions when input changes
  useEffect(() => {
    if (inputValue.length >= 1) {
      setSelectedIndex(-1) // Reset selection when new search starts
      const timeoutId = setTimeout(() => {
        searchRecipients(inputValue, (_error, _result) => {
          // Results are handled by the hook's internal state
        })
      }, 300) // Debounce search

      return () => clearTimeout(timeoutId)
    } else {
      setSelectedIndex(-1)
    }
  }, [inputValue, searchRecipients])

  // Clean up on unmount
  useEffect(() => {
    return cleanup
  }, [cleanup])

  // Handle clicks outside to close dropdown
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setClickedOutside(true)
    }
  }, [])

  useEventListener('mousedown', handleClickOutside)

  const getScrollContainer = useCallback(() => {
    return containerRef.current?.querySelector(
      `.${styles.inputContainer}`,
    ) as HTMLElement | null
  }, [])

  const scrollContainerToBottom = useCallback(() => {
    // Scroll the recipient container to bottom to show newly added emails
    const container = getScrollContainer()
    if (container) {
      container.scrollTop = container.scrollHeight
    }
  }, [getScrollContainer])

  /**
   * Execute an action while preserving the scroll position of the input container.
   * Useful for operations that might cause layout shifts (like removing recipients).
   */
  const withScrollPreservation = useCallback(
    (action: () => void) => {
      const container = getScrollContainer()
      const scrollTop = container?.scrollTop ?? 0
      action()
      setTimeout(() => {
        if (container) {
          container.scrollTop = scrollTop
        }
      }, 0)
    },
    [getScrollContainer],
  )

  const handleBlur = useCallback(() => {
    // Delay to allow dropdown clicks
    setTimeout(() => {
      if (
        inputValue.trim() &&
        validateEmail(inputValue.trim()) &&
        !isSelectingFromDropdown.current
      ) {
        onAdd(inputValue.trim())
        setInputValue('')
      }
      isSelectingFromDropdown.current = false
    }, 150)
  }, [inputValue, onAdd])

  /**
   * Parse a string containing multiple emails separated by delimiters
   * Supported delimiters: space, comma, semicolon, tab, newline
   * @param text - The text containing emails to parse
   * @returns Array of trimmed email strings
   */
  const parseEmailList = useCallback((text: string): string[] => {
    // Split by common delimiters: space, comma, semicolon, tab, newline
    const emails = text
      .split(/[\s,;\t\n]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0)

    return emails
  }, [])

  /**
   * Handle paste events to support bulk email addition
   * Parses pasted text for multiple emails and adds valid ones
   */
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text')

      // Check if the pasted text contains multiple emails (has delimiters)
      const hasDelimiters = /[\s,;\t\n]/.test(pastedText)

      if (hasDelimiters) {
        e.preventDefault() // Prevent default paste behavior

        const emails = parseEmailList(pastedText)

        // Filter out empty strings and validate emails
        const validEmails = emails.filter(email => validateEmail(email))

        // Add all valid emails
        validEmails.forEach(email => {
          // Check for duplicates before adding
          const isDuplicate = recipients.some(
            r => r.email?.toLowerCase() === email.toLowerCase(),
          )
          if (!isDuplicate) {
            onAdd(email)
          }
        })

        // Clear the input
        setInputValue('')

        // Scroll to bottom to show newly added emails
        setTimeout(() => {
          scrollContainerToBottom()
        }, 0)
      }
      // If no delimiters, allow default paste behavior (single email)
    },
    [parseEmailList, recipients, onAdd, scrollContainerToBottom],
  )

  const handleBadgeClick = useCallback(
    (e: React.MouseEvent, recipientId: string) => {
      e.preventDefault()
      withScrollPreservation(() => onRemove(recipientId))
      setTimeout(() => inputRef.current?.focus(), 0)
    },
    [onRemove, withScrollPreservation],
  )

  const handleContainerClick = (e: React.MouseEvent) => {
    // Don't interfere if clicking on the input directly
    if (e.target === inputRef.current) {
      return
    }

    // Focus the input and ensure it's actually focused
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const handleDropdownSelect = useCallback(
    option => {
      isSelectingFromDropdown.current = true
      if (option.type === 'saved' || option.type === 'brief') {
        option.recipients.forEach(recipient => {
          // brief exposes contact_id, saved exposes it as recipient.id.
          const contactId = recipient.contact_id ?? recipient.id ?? undefined
          onAdd(recipient.email || undefined, recipient.name, contactId)
        })
      } else {
        const contactId =
          option.type === 'user' && option.id
            ? Number.parseInt(option.id, 10)
            : undefined
        onAdd(
          option.email || undefined,
          option.name,
          Number.isFinite(contactId) ? contactId : undefined,
        )
      }
      setInputValue('')
      setSelectedIndex(-1)
      // Focus back to input and scroll to bottom after a short delay
      setTimeout(() => {
        inputRef.current?.focus()
        scrollContainerToBottom()
        isSelectingFromDropdown.current = false
      }, 0)
    },
    [onAdd, scrollContainerToBottom],
  )

  const handleCreateNew = useCallback(
    (rawInput: string) => {
      isSelectingFromDropdown.current = true
      const parsed = getEmailHandleFromAngularBrackets(rawInput)
      if (validateEmail(parsed.email)) {
        onAdd(parsed.email, parsed.name)
      } else {
        onAdd(undefined, rawInput)
      }
      setInputValue('')
      setSelectedIndex(-1)
      setTimeout(() => {
        inputRef.current?.focus()
        scrollContainerToBottom()
        isSelectingFromDropdown.current = false
      }, 0)
    },
    [onAdd, scrollContainerToBottom],
  )

  const handleInputFocus = () => {
    setClickedOutside(false)
  }

  // Check if dropdown is actually visible (when there are suggestions, loading, error, or valid email)
  const isDropdownVisible: boolean = showDropdown && !clickedOutside

  useEffect(() => {
    if (!isDropdownVisible) setSelectedIndex(-1)
  }, [isDropdownVisible])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isDropdownVisible && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault()
        const totalOptions = dropdownOptions.length + (shouldShowCreate ? 1 : 0)

        if (e.key === 'ArrowDown') {
          setSelectedIndex(prev => (prev < totalOptions - 1 ? prev + 1 : prev))
        } else if (e.key === 'ArrowUp') {
          setSelectedIndex(prev => (prev > -1 ? prev - 1 : -1))
        }
        return
      }

      // Handle scrolling the recipient container when dropdown is not visible
      if (
        !isDropdownVisible &&
        (e.key === 'ArrowDown' || e.key === 'ArrowUp')
      ) {
        e.preventDefault()
        const container = getScrollContainer()
        if (container) {
          const scrollAmount = 20
          container.scrollTop +=
            e.key === 'ArrowDown' ? scrollAmount : -scrollAmount
        }
        return
      }

      if (e.key === 'Tab' && isDropdownVisible) {
        const option = selectedIndex >= 0 ? suggestionAt(selectedIndex) : null

        if (option) {
          isSelectingFromDropdown.current = true
          if (
            (option.type === 'saved' || option.type === 'brief') &&
            'recipients' in option &&
            Array.isArray(option.recipients)
          ) {
            ;(
              option.recipients as Array<{
                id?: number
                email: string
                name?: string
                contact_id?: number | null
              }>
            ).forEach(r => {
              const contactId = r.contact_id ?? r.id ?? undefined
              onAdd(r.email || undefined, r.name, contactId)
            })
          } else {
            const contactId =
              option.type === 'user' && option.id
                ? Number.parseInt(option.id, 10)
                : undefined
            onAdd(
              option.email || undefined,
              option.name,
              Number.isFinite(contactId) ? contactId : undefined,
            )
          }
          setInputValue('')
          setSelectedIndex(-1)
        } else if (shouldShowCreate) {
          isSelectingFromDropdown.current = true
          const trimmed = inputValue.trim()
          if (validateEmail(trimmed)) {
            onAdd(trimmed)
          } else {
            onAdd(undefined, trimmed)
          }
          setInputValue('')
          setSelectedIndex(-1)
        }
        setClickedOutside(true)
        return
      }

      if (e.key === 'Escape') {
        if (isDropdownVisible) {
          e.preventDefault()
          e.stopPropagation()
          setClickedOutside(true)
        }
        return
      }

      if (e.key === 'Enter') {
        e.preventDefault()

        if (isDropdownVisible && selectedIndex >= 0) {
          const selectedSuggestion = suggestionAt(selectedIndex)
          if (selectedSuggestion) {
            handleDropdownSelect(selectedSuggestion)
          } else {
            handleCreateNew(inputValue.trim())
          }
          setSelectedIndex(-1)
        } else if (inputValue.trim()) {
          if (allowCreateRecipient || validateEmail(inputValue.trim())) {
            handleCreateNew(inputValue.trim())
            setSelectedIndex(-1)
          }
        }
      }

      if (e.key === 'Backspace' && !inputValue && recipients.length > 0) {
        withScrollPreservation(() =>
          onRemove(recipients[recipients.length - 1].id),
        )
      }
    },
    [
      inputValue,
      recipients,
      onAdd,
      onRemove,
      selectedIndex,
      dropdownOptions,
      handleDropdownSelect,
      handleCreateNew,
      isDropdownVisible,
      allowCreateRecipient,
      withScrollPreservation,
      getScrollContainer,
      suggestionAt,
      shouldShowCreate,
    ],
  )

  return (
    <>
      <Box ref={containerRef} className={styles.container}>
        <Text size="2" weight="medium" mb="2" as="label" htmlFor={inputId}>
          Enter email addresses or search for contacts, mailing lists, or briefs
        </Text>

        <Box
          aria-label={ariaLabel}
          onClick={handleContainerClick}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              inputRef.current?.focus()
            }
          }}
          mt="1"
          className={styles.inputContainer}
        >
          <DsFlex gap="1" align="start" wrap="wrap">
            {recipients.map(recipient => {
              const label = recipient.name || recipient.email
              const isNewContact = recipient.contactId == null
              return (
                <Badge
                  key={recipient.id}
                  variant="soft"
                  color={recipient.isValid ? 'blue' : 'red'}
                  role="button"
                  tabIndex={0}
                  aria-label={
                    isNewContact
                      ? `Remove new contact ${label}`
                      : `Remove ${label}`
                  }
                  className={styles.recipientBadge}
                  onClick={e => handleBadgeClick(e, recipient.id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      withScrollPreservation(() => onRemove(recipient.id))
                    }
                  }}
                >
                  {isNewContact && (
                    <Text size="1" weight="bold" mr="4" aria-hidden="true">
                      +
                    </Text>
                  )}
                  <span>{label}</span>
                  <Text
                    size="1"
                    weight="bold"
                    ml="2"
                    className={styles.recipientRemove}
                    aria-hidden="true"
                  >
                    ×
                  </Text>
                </Badge>
              )
            })}

            <input
              ref={inputRef}
              id={inputId}
              type="email"
              value={inputValue}
              onChange={e => {
                setInputValue(e.target.value)
                if (clickedOutside) setClickedOutside(false)
              }}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onFocus={handleInputFocus}
              onPaste={handlePaste}
              placeholder={recipients.length === 0 ? placeholder : ''}
              aria-label="Add email address"
              aria-expanded={isDropdownVisible}
              aria-autocomplete="list"
              aria-controls="recipient-dropdown"
              role="combobox"
              className={styles.emailInput}
            />
          </DsFlex>
        </Box>

        <RecipientDropdown
          inputValue={inputValue}
          suggestions={dropdownOptions}
          loading={loading}
          onSelect={handleDropdownSelect}
          onCreateNew={handleCreateNew}
          showDropdown={isDropdownVisible}
          selectedIndex={selectedIndex}
          onMouseEnterOption={setSelectedIndex}
          apiError={apiError}
          allowCreateRecipient={allowCreateRecipient}
        />
      </Box>

      {/* Show validation errors */}
      {recipients.some(r => !r.isValid) && (
        <Text size="1" color="red" mt="1">
          Some email addresses are invalid and will not be included.
        </Text>
      )}
    </>
  )
}
